import {padRect} from '@base/dom-utils/rect'
import {safeNormalizeScroll} from '@base/dom-utils/scroll'

import * as strategies from './strategies'
import type {
  StrategyFn,
  Placements,
  ElementOrRectangleOrFn,
  Rectangle,
  DynamicRectanglesOptions,
  RectanglesOptions,
} from './types'

function resolveStrategy(strategy: StrategyFn | string) {
  if (typeof strategy === 'function') {
    return strategy
    // eslint-disable-next-line import/namespace
  } else if (strategies[strategy]) {
    // eslint-disable-next-line import/namespace
    return strategies[strategy]
  }
  throw new Error(`Unknown tether strategy '${strategy}'`)
}

export function resolvePlacements(placements: Placements) {
  if (Array.isArray(placements)) {
    return placements.map(resolveStrategy)
  } else if (typeof placements === 'string') {
    return placements.split(',').map(resolveStrategy)
  } else if (typeof placements === 'function') {
    return [placements]
  }
  throw new Error('wrong placements')
}

export function resolveRectangle(
  elementOrRect?: ElementOrRectangleOrFn | Window | null
): Rectangle | undefined {
  if (!elementOrRect) {
    return undefined
  }
  if (typeof elementOrRect === 'function') {
    return resolveRectangle(elementOrRect())
  }
  // @ts-ignore
  if (elementOrRect instanceof Element || elementOrRect === window) {
    return safeNormalizeScroll(elementOrRect).getAbsoluteBorderBox()
  }
  if (
    !elementOrRect.hasOwnProperty('left') ||
    !elementOrRect.hasOwnProperty('top') ||
    !elementOrRect.hasOwnProperty('width') ||
    !elementOrRect.hasOwnProperty('height')
  ) {
    throw new Error('left, top, width & height is required in a rect')
  }
  // @ts-ignore
  return elementOrRect
}

export function resolveRectangles({
  element,
  target,
  container,
  targetContainer,
  containerPadding,
}: DynamicRectanglesOptions): RectanglesOptions | undefined | null {
  const elementRect = resolveRectangle(element)
  if (!elementRect) return null

  const targetRect = resolveRectangle(target)
  if (!targetRect) return null

  const containerRect = resolveRectangle(container)
  // if containerRect is specified, but it's not resolved, it means it's not ready yet
  if (!containerRect && container) {
    return null
  }

  const targetContainerRect = resolveRectangle(targetContainer)
  if (!targetContainerRect && targetContainer) {
    return null
  }

  return {
    elementRect,
    targetRect,
    containerRect: containerRect
      ? padRect(containerRect, Number(containerPadding))
      : undefined,
    visibleRect: containerRect,
    targetVisibleRect: targetContainerRect || containerRect,
  }
}
